import { useAuthentication } from "@src/auth"
import { TrackerSelector } from "@src/components/TrackerSelector"
import { config } from "@src/constants"
import { usePartner } from "@src/partner"
import { useUser } from "@src/user"
import React from "react"
import { Button, Dropdown, DropdownButton } from "react-bootstrap"
import { useTranslation } from "react-i18next"

const styles = {
  serialNumberInput: {
    marginTop: "15px",
  },
}

export const BookTrackerService: React.FC = () => {
  const { t } = useTranslation()
  const { userEmail } = useAuthentication()
  const { partner } = usePartner()
  const { user } = useUser()
  const [serialNumbers, setSerialNumbers] = React.useState("")
  const [duration, setDuration] = React.useState(12)
  const [selectedDetails, setSelectedDetails] = React.useState<
    { serialnumber: string; name: string }[]
  >([])

  const tariff = [12, 24, 36]

  const monthYear = (item: number) => {
    const years = item / 12
    const key =
      years === 1
        ? "settings.partner.book_service.month_year"
        : "settings.partner.book_service.month_years"
    return t(key, { month_value: item, year_value: years })
  }

  const selectedSerials = React.useMemo(
    () =>
      Array.from(
        new Set(
          serialNumbers
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ),
    [serialNumbers],
  )

  const handleSelectorChange = (serials: string[]) => {
    setSerialNumbers(serials.join("; "))
  }

  const createEmailLink = () => {
    const subject = "Service Extension Request for My Trackers"

    const body = `Dear Customer Care Team,

I would like to request a service extension for my trackers through your fleet portal.

My account details:
- Email: ${userEmail}
- Name: ${user?.firstname} ${user?.lastname}
- Address: ${user?.address}
- Partner: ${partner?.name} (${partner?.uuid})

Tracker details:
- Serial Numbers: ${serialNumbers || "[Please fill in serial numbers]"}
- Requested service period: ${duration} months

Could you please contact me to discuss the pricing options for this service extension?

Thank you for your assistance.

Best regards,
${user?.firstname} ${user?.lastname}`

    return `mailto:${config.customerCareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <div>
      <div>
        <h5>{t("settings.partner.book_service_title")}</h5>
        <p className="mb-3">{t("settings.partner.book_service_description")}</p>

        <div className="mb-2" style={styles.serialNumberInput}>
          <label className="form-label">
            {t("settings.partner.book_service_serial_numbers_label")}:
          </label>
          <div className="mb-2 d-flex flex-wrap gap-2">
            {selectedDetails.map((d) => (
              <span key={d.serialnumber} className="badge bg-secondary">
                {d.serialnumber} - {d.name}
                <span
                  role="button"
                  aria-label="remove"
                  className="ms-2"
                  onClick={() =>
                    handleSelectorChange(selectedSerials.filter((s) => s !== d.serialnumber))
                  }
                >
                  ×
                </span>
              </span>
            ))}
          </div>
          <div className="mt-2">
            <TrackerSelector
              selectedSerials={selectedSerials}
              onChange={handleSelectorChange}
              onChangeDetails={setSelectedDetails}
              allowFilter
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label">
            {t("settings.partner.book_service.service_duration")}:
          </label>
          <DropdownButton
            className="mt-2"
            id="tariff-picker"
            title={monthYear(duration)}
            variant="outline-secondary"
          >
            {tariff.map((item) => (
              <Dropdown.Item key={item} onClick={() => setDuration(item)}>
                {monthYear(item)}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>

        <Button variant="primary" href={createEmailLink()} disabled={!serialNumbers.trim()}>
          {t("settings.partner.send_email_button")}
        </Button>
      </div>
    </div>
  )
}
